import { GetStaticPaths } from 'next';
import { GetStaticPropsContext } from 'next/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { TLocale, ISeo } from '@typescript/index';
import { IParams } from '@features/categories';
import { getLayoutData } from '@features/layout/api';
import { IContentNavigation, TCmsBlocks } from '@features/cms/typescript';
import { IAppearanceObject } from '@features/pages/typescript';
import { getSiteCmsDocumentBySlug } from '@features/cms/services/CmsServices';
import CmsDocument from '@features/cms/objects/CmsDocument';
import RenderCmsBlock from '@features/cms/components/RenderCmsBlock';
import MainSeo from '@components/MainSeo/MainSeo';
import { REVALIDATION_TIME } from '@config/index';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import theme from '@styles/theme';
import ContentNavigation from '@components/ContentNavigation/ContentNavigation';
import getNavigationVariant from '@utils/getNavigationVariant';

type PropsType = {
  seo: ISeo | undefined;
  appearance: IAppearanceObject | undefined;
  contentNavigation: IContentNavigation[] | null | undefined;
  content: Record<string, TCmsBlocks> | null;
  isBuildTime?: boolean;
};

const MainPageDynamic = ({ seo, appearance, contentNavigation, content, isBuildTime }: PropsType) => {
  if (isBuildTime) {
    return <div>Build time</div>;
  }
  const withNavigation = !!(contentNavigation && contentNavigation.length);
  return (
    <>
      <SelfCanonicalLink />
      {withNavigation && <ContentNavigation links={contentNavigation} />}
      <main
        id="main-content"
        sx={{
          background: appearance?.backgroundColor ?? theme.rawColors.neutralWhite,
        }}
      >
        {seo && <MainSeo seo={seo} />}
        {content &&
          Object.keys(content).map((key, index) => (
            <RenderCmsBlock
              key={key}
              block={content[key]}
              isFirst={!index}
              defaultBackground={appearance?.backgroundColor || theme.rawColors.neutralWhite}
            />
          ))}
      </main>
    </>
  );
};

export const getStaticProps = async ({ params, locale }: GetStaticPropsContext<IParams>) => {
  if (process.env.IS_BUILD) {
    return {
      props: {
        isBuildTime: true,
      },
      revalidate: 1,
    };
  }
  if (params === undefined) {
    return new Error('Params missing');
  }

  const { mainCategory: slug } = params;

  const isSlugCorrect = /^[A-z0-9-/]*$/.test(slug);
  if (!isSlugCorrect) {
    return {
      notFound: true,
      revalidate: true,
    };
  }

  const layoutData = await getLayoutData(locale as TLocale);

  let document: null | CmsDocument = null;

  try {
    document = await getSiteCmsDocumentBySlug(locale as TLocale, slug);
    // biome-ignore lint/correctness/noUnusedVariables: intentional
  } catch (error) {
    return { notFound: true, revalidate: true };
  }

  if (!document || !document?.id) return { notFound: true, revalidate: true };

  const { seo, appearance, contentNavigation } = document;
  const content = await document.getAdaptedContent();

  const navParams = getNavigationVariant(
    document.appearance?.navigationVariant,
    content ?? {},
    document.appearance?.backgroundColor ?? 'neutralWhite',
  );

  return {
    props: {
      ...getDevProps('static', REVALIDATION_TIME),
      seo,
      appearance,
      contentNavigation,
      content,
      isBuildTime: false,
      ...layoutData,
      ...(await serverSideTranslations(locale as string)),
      navParams,
    },
    revalidate: REVALIDATION_TIME,
  };
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
});

export default MainPageDynamic;
